import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { formatStamp } from "../lib/richText";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { NewProspectAuditDialog } from "../components/prospect-audit/NewProspectAuditDialog";
import { scoreTone } from "../components/prospect-audit/scoreTone";
import { Gauge, Plus, MapPin, Loader2 } from "lucide-react";

export default function ProspectAuditsPage() {
  const [rows, setRows] = useState([]);
  const [quota, setQuota] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([axios.get(`${API}/prospect-audits`), axios.get(`${API}/prospect-audits/quota`)])
      .then(([r, q]) => { setRows(r.data || []); setQuota(q.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="prospect-audits-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2"><Gauge className="h-7 w-7 text-primary" />Prospect Audit</h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
            One report: Website Score (speed + 64 checks), SEO &amp; AEO grades, and an optional Google check — map grid, Google profile, search demand and real AI answers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {quota && quota.limit !== null && (
            <span className="text-xs px-3 py-1.5 rounded-full border bg-white" data-testid="google-quota-chip">
              <MapPin className="h-3 w-3 inline mr-1 text-primary" />{quota.remaining} of {quota.limit} Google checks left this month
            </span>
          )}
          <Button onClick={() => setOpen(true)} disabled={quota && !quota.configured} data-testid="new-prospect-audit-btn">
            <Plus className="h-4 w-4 mr-2" />New Prospect Audit
          </Button>
        </div>
      </div>
      {quota && !quota.configured && <p className="text-sm text-amber-700" data-testid="grader-not-connected">The Website Grader isn&apos;t connected yet.</p>}
      <NewProspectAuditDialog open={open} onOpenChange={setOpen} quota={quota} onCreated={(id) => navigate(`/prospect-iq/audits/${id}`)} />
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : rows.length === 0 ? (
        <Card className="border-dashed p-12 text-center text-sm text-muted-foreground">No reports yet. Run your first Prospect Audit.</Card>
      ) : (
        <div className="grid gap-3" data-testid="prospect-audits-list">
          {rows.map((r) => (
            <Card key={r.id} onClick={() => navigate(`/prospect-iq/audits/${r.id}`)} data-testid={`prospect-audit-row-${r.id}`}
              className="p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center font-bold text-lg ${scoreTone(r.grader?.overall).chip}`}>
                {r.status === "running" && r.grader?.overall == null ? <Loader2 className="h-5 w-5 animate-spin" /> : (r.grader?.overall ?? "—")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{r.business_name}</div>
                <div className="text-xs text-muted-foreground truncate">{r.website_url} · {r.created_by_name} · {formatStamp(r.created_at)}</div>
              </div>
              <div className="hidden sm:flex gap-2">
                {r.seo_aeo?.seo_grade && <Badge variant="outline">SEO {r.seo_aeo.seo_grade}</Badge>}
                {r.seo_aeo?.aeo_grade && <Badge variant="outline">AEO {r.seo_aeo.aeo_grade}</Badge>}
                {r.wants_google && <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">Map top 3: {r.google?.summary?.top3Pct ?? "…"}%</Badge>}
                {r.status === "running" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Running</Badge>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
